import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SHOW_IMAGE_GENERATION_VIEW } from 'js/actionCreators/ImageDrawerActions';

import {
  isEditorCloseAction,
  isGenerateScriptFailedAction,
  isShowLeftHandPanelAction
} from './reducerHelpers/actionValidators';
import { goBackToScript } from './voiceoversReducer';

export type ProjectScript = {
  id: number;
  projectScript: string;
  projectId: number;
  title?: string;
  dateCreated: string;
  dateModified: string;
};

type ProjectScriptsState = {
  projects: Record<
    number,
    {
      state: 'loading' | 'loaded' | 'error';
      scripts: ProjectScript[];
    }
  >;
  editingScript: ProjectScript | Partial<ProjectScript> | null;
  aiDropdownOpen: boolean;
  syncStatus: 'saving' | 'saved' | 'error' | null;
};

const initialState: ProjectScriptsState = {
  projects: {},
  editingScript: null,
  aiDropdownOpen: false,
  syncStatus: null
};

const projectScriptsSlice = createSlice({
  name: 'projectScripts',
  initialState,
  reducers: {
    getProjectScripts: (state, action: PayloadAction<{ projectId: number }>) => {
      const { projectId } = action.payload;
      if (!state.projects[projectId]) {
        state.projects[projectId] = {
          state: 'loading',
          scripts: []
        };
      }
    },
    getProjectScriptsSuccess: (state, action: PayloadAction<{ projectId: number; scripts: ProjectScript[] }>) => {
      const { projectId, scripts } = action.payload;
      state.projects[projectId] = {
        state: 'loaded',
        scripts
      };
    },
    getProjectScriptsError: (state, action: PayloadAction<{ projectId: number }>) => {
      const { projectId } = action.payload;
      state.projects[projectId] = {
        state: 'error',
        scripts: []
      };
    },
    setEditingScript: (state, action: PayloadAction<{ script: ProjectScript | Partial<ProjectScript> }>) => {
      state.syncStatus = 'saved';
      state.editingScript = action.payload.script;
    },
    startNewScript: (state, action: PayloadAction<{ projectId?: number; aiDropdownOpen: boolean }>) => {
      state.syncStatus = null;
      state.editingScript = {
        title: '',
        projectScript: '',
        projectId: action.payload.projectId
      };
      state.aiDropdownOpen = action.payload.aiDropdownOpen;
    },
    clearEditingScript: state => {
      state.editingScript = null;
      state.syncStatus = null;
    },
    updateEditingScript: (state, action: PayloadAction<{ script: Partial<ProjectScript> }>) => {
      if (state.editingScript) {
        state.syncStatus = 'saving';
        state.editingScript = {
          ...state.editingScript,
          ...action.payload.script,
          title: action.payload.script.title?.trim() ?? state.editingScript.title,
          projectScript: action.payload.script.projectScript?.trim() ?? state.editingScript.projectScript
        };
      }
    },
    syncSuccess: (state, action: PayloadAction<{ script: ProjectScript }>) => {
      const updatedScript = action.payload.script;
      state.syncStatus = 'saved';

      if (state.editingScript && !state.editingScript.id) {
        state.editingScript.id = updatedScript.id;
      }

      state.projects[updatedScript.projectId] = {
        state: 'loaded',
        scripts: [
          updatedScript,
          ...(state.projects[updatedScript.projectId]?.scripts || []).filter(script => script.id !== updatedScript.id)
        ]
      };
    },
    syncError: state => {
      state.syncStatus = 'error';
    },
    deleteScript: (state, action: PayloadAction<{ scriptId: number; projectId: number }>) => {
      const { scriptId, projectId } = action.payload;
      if (state.projects[projectId]) {
        state.projects[projectId].scripts = state.projects[projectId].scripts.filter(script => script.id !== scriptId);
      }
    },
    setAiDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.aiDropdownOpen = action.payload;
    }
  },
  selectors: {
    selectProjectScripts: (state: ProjectScriptsState, projectId: number) => {
      return state.projects[projectId];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(goBackToScript, (state, action) => {
        state.editingScript = action.payload.script;
      })
      .addCase(SHOW_IMAGE_GENERATION_VIEW, state => {
        state.editingScript = null;
      })
      .addMatcher(isEditorCloseAction, state => {
        state.editingScript = null;
      })
      .addMatcher(isGenerateScriptFailedAction, state => {
        state.aiDropdownOpen = true;
      })
      .addMatcher(isShowLeftHandPanelAction, state => {
        state.editingScript = null;
      });
  }
});

export default projectScriptsSlice.reducer;
export const {
  getProjectScripts,
  getProjectScriptsSuccess,
  getProjectScriptsError,
  setEditingScript,
  startNewScript,
  clearEditingScript,
  updateEditingScript,
  syncSuccess,
  syncError,
  deleteScript,
  setAiDropdownOpen
} = projectScriptsSlice.actions;
export const { selectProjectScripts } = projectScriptsSlice.selectors;
